import { ref } from 'vue'
import type { AdminStorageQuota, StorageQuotaOverridePayload } from '@/types/organisations'
import {
  clearStorageQuotaOverride,
  fetchStorageQuotaOverride,
  updateStorageQuotaOverride,
} from '@/api/storageQuota'

export function useOrganisationStorageQuota() {
  const quota = ref<AdminStorageQuota | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const actionLoading = ref(false)
  const actionError = ref<string | null>(null)
  const fieldErrors = ref<Record<string, string[]>>({})

  // GET /admin/organisations/{organisation}/storage-quota-override. The quota
  // resolution (override vs platform default) is done server-side only.
  async function fetchQuota(organisationId: number): Promise<void> {
    loading.value = true
    error.value = null
    try {
      quota.value = await fetchStorageQuotaOverride(organisationId)
    } catch {
      error.value = 'admin.common.error_loading'
    } finally {
      loading.value = false
    }
  }

  async function setOverride(organisationId: number, payload: StorageQuotaOverridePayload): Promise<boolean> {
    return runAction(async () => {
      quota.value = await updateStorageQuotaOverride(organisationId, payload)
    })
  }

  // DELETE returns the resolved (now platform-default) state, so no refetch is needed.
  async function clearOverride(organisationId: number, reason: string): Promise<boolean> {
    return runAction(async () => {
      quota.value = await clearStorageQuotaOverride(organisationId, reason)
    })
  }

  async function runAction(action: () => Promise<void>): Promise<boolean> {
    actionLoading.value = true
    actionError.value = null
    fieldErrors.value = {}
    try {
      await action()
      return true
    } catch (e: unknown) {
      if (is422Error(e)) {
        fieldErrors.value = extractFieldErrors(e) ?? {}
      } else {
        actionError.value = 'admin.common.error_generic'
      }
      return false
    } finally {
      actionLoading.value = false
    }
  }

  return {
    quota,
    loading,
    error,
    actionLoading,
    actionError,
    fieldErrors,
    fetchQuota,
    setOverride,
    clearOverride,
  }
}

function is422Error(e: unknown): boolean {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: { status: number } }).response.status === 422
  )
}

function extractFieldErrors(e: unknown): Record<string, string[]> | null {
  if (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object'
  ) {
    const resp = (e as { response: { data?: { errors?: Record<string, string[]> } } }).response
    return resp.data?.errors ?? null
  }
  return null
}
